/**
 * Persona Configuration Registry
 *
 * Centralized persona definitions used by usePersona hook,
 * onboarding flow, and dashboard layout.
 *
 * @module config/personas
 */
import {
  Code2,
  Rocket,
  Building2,
  Scale,
  TrendingUp,
  Target,
  Terminal,
} from 'lucide-react';

export const PERSONA_TYPES = {
  PROFESSIONAL: 'professional',
  FOUNDER: 'founder',
  ARCHITECT: 'architect',
  ANALYST: 'analyst',
  INVESTOR: 'investor',
  PRODUCT_OWNER: 'product_owner',
  DEVELOPER: 'developer',
};

export const DEFAULT_PERSONA = PERSONA_TYPES.PROFESSIONAL;

export const PERSONA_CONFIGS = {
  [PERSONA_TYPES.PROFESSIONAL]: {
    id: 'professional',
    title: 'Individual Professional',
    subtitle: 'Career intelligence from your code',
    icon: Code2,
    color: 'primary',
    // Design system v2 persona color token (DESIGN.md)
    accentToken: 'persona-individual',  // violet-400 #a78bfa
    accentClass: 'text-violet-400',
    accentBg: 'bg-violet-400/[0.08]',
    accentBorder: 'border-violet-400/20',
    defaultTab: 'overview',
    featuredActions: ['import', 'skills', 'export'],
    onboardingVariant: 'individual',
    description:
      'Build a verified portfolio that proves your skills through actual code. Get AI-powered career insights, skill gap analysis, and a dynamic CV.',
    seo: {
      title: 'Developer Portfolio Intelligence | Portfolistic',
      description: 'Build a verified portfolio from your GitHub repos. AI-powered skill analysis, career insights, and dynamic CV generation.',
      keywords: ['developer portfolio', 'skill analysis', 'career intelligence', 'github portfolio', 'code analysis'],
    },
  },
  [PERSONA_TYPES.FOUNDER]: {
    id: 'founder',
    title: 'Startup Founder',
    subtitle: 'Tell your technical story',
    icon: Rocket,
    color: 'success',
    accentToken: 'persona-founder',     // teal-400 #2dd4bf
    accentClass: 'text-teal-400',
    accentBg: 'bg-teal-400/[0.08]',
    accentBorder: 'border-teal-400/20',
    defaultTab: 'value',
    featuredActions: ['import', 'value', 'sharing'],
    onboardingVariant: 'team',
    description:
      'Generate investor-ready due diligence packages. Showcase team capabilities and technology maturity backed by code evidence.',
    seo: {
      title: 'Startup Technical Due Diligence | Portfolistic',
      description: 'Generate investor-ready due diligence packages. Showcase team capabilities and technology maturity backed by code evidence.',
      keywords: ['startup due diligence', 'investor reports', 'team analysis', 'technical dd', 'fundraising'],
    },
  },
  [PERSONA_TYPES.ARCHITECT]: {
    id: 'architect',
    title: 'Enterprise Architect',
    subtitle: 'Data-driven build vs buy',
    icon: Building2,
    color: 'secondary',
    accentToken: 'persona-architect',   // blue-400 #60a5fa
    accentClass: 'text-blue-400',
    accentBg: 'bg-blue-400/[0.08]',
    accentBorder: 'border-blue-400/20',
    defaultTab: 'value',
    featuredActions: ['import', 'value', 'discovery'],
    onboardingVariant: 'enterprise',
    description:
      'Evaluate acquisition targets and integration complexity. Get technical fit scores and architecture compatibility analysis.',
    seo: {
      title: 'Build vs Buy Analysis | Portfolistic',
      description: 'Data-driven build vs buy decisions. Evaluate acquisition targets with technical fit scores and architecture compatibility analysis.',
      keywords: ['build vs buy', 'enterprise architecture', 'technical evaluation', 'integration assessment', 'acquisition analysis'],
    },
  },
  [PERSONA_TYPES.ANALYST]: {
    id: 'analyst',
    title: 'M&A Analyst',
    subtitle: "Know what you're buying",
    icon: Scale,
    color: 'warning',
    accentToken: 'persona-analyst',     // slate-300 #cbd5e1
    accentClass: 'text-slate-300',
    accentBg: 'bg-slate-300/[0.08]',
    accentBorder: 'border-slate-300/20',
    defaultTab: 'value',
    featuredActions: ['import', 'value', 'export'],
    onboardingVariant: 'enterprise',
    description:
      'Multi-methodology valuation of technical assets with IP assessment, risk scoring, and comparable analysis.',
    seo: {
      title: 'M&A Technical Valuation | Portfolistic',
      description: 'Multi-methodology valuation of technical assets. IP assessment, risk scoring, and comparable transaction analysis.',
      keywords: ['ma valuation', 'technical assets', 'ip assessment', 'comparable analysis', 'technology valuation'],
    },
  },
  [PERSONA_TYPES.INVESTOR]: {
    id: 'investor',
    title: 'VC / PE Investor',
    subtitle: 'Technical due diligence',
    icon: TrendingUp,
    color: 'primary',
    accentToken: 'persona-investor',    // amber-300/gold #fcd34d
    accentClass: 'text-amber-300',
    accentBg: 'bg-amber-300/[0.08]',
    accentBorder: 'border-amber-300/20',
    defaultTab: 'value',
    featuredActions: ['value', 'skills', 'sharing'],
    onboardingVariant: 'enterprise',
    description:
      'Assess team capability, scalability bottlenecks, and tech stack risk without scheduling calls.',
    seo: {
      title: 'VC Portfolio Intelligence | Portfolistic',
      description: 'Assess team capability, scalability, and tech stack risk across your portfolio without scheduling calls.',
      keywords: ['vc due diligence', 'portfolio monitoring', 'startup assessment', 'technical risk', 'investor tools'],
    },
  },
  [PERSONA_TYPES.PRODUCT_OWNER]: {
    id: 'product_owner',
    title: 'Product Owner',
    subtitle: 'Map team capabilities',
    icon: Target,
    color: 'secondary',
    accentToken: 'persona-pm',          // cyan-300 #67e8f9
    accentClass: 'text-cyan-300',
    accentBg: 'bg-cyan-300/[0.08]',
    accentBorder: 'border-cyan-300/20',
    defaultTab: 'skills',
    featuredActions: ['import', 'skills', 'collections'],
    onboardingVariant: 'team',
    description:
      'Understand your team capabilities and roadmap feasibility. Get skill coverage maps and feature-to-capability matching.',
    seo: {
      title: 'Team Capability Mapping | Portfolistic',
      description: 'Understand your team capabilities and roadmap feasibility. Skill coverage maps and feature-to-capability matching.',
      keywords: ['team capabilities', 'skill mapping', 'roadmap feasibility', 'product planning', 'capability assessment'],
    },
  },
  [PERSONA_TYPES.DEVELOPER]: {
    id: 'developer',
    title: 'API Developer',
    subtitle: 'Portfolio intelligence via API',
    icon: Terminal,
    color: 'success',
    accentToken: 'persona-developer',   // orange-400 #fb923c
    accentClass: 'text-orange-400',
    accentBg: 'bg-orange-400/[0.08]',
    accentBorder: 'border-orange-400/20',
    defaultTab: 'account',
    featuredActions: ['import', 'account'],
    onboardingVariant: 'developer',
    description:
      'Integrate skill analysis, project valuation, and team metrics into your own tools via RESTful API.',
    seo: {
      title: 'Portfolio Intelligence API | Portfolistic',
      description: 'Integrate skill analysis, project valuation, and team metrics into your own tools via RESTful API.',
      keywords: ['portfolio api', 'developer sdk', 'skill analysis api', 'project valuation api', 'team metrics'],
    },
  },
};

/**
 * Get persona config by type
 * @param {string} personaType - One of PERSONA_TYPES values
 * @returns {object} Persona configuration object
 */
export const getPersonaConfig = (personaType) => {
  return PERSONA_CONFIGS[personaType] || PERSONA_CONFIGS[DEFAULT_PERSONA];
};

/**
 * Get all persona configs as array (for rendering lists)
 * @returns {object[]} Array of persona configuration objects
 */
export const getAllPersonas = () => {
  return Object.values(PERSONA_CONFIGS);
};
